import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getNationalitiesList } from '../../../api/countries/get-nationalities-list'
import { useNativeCountryField } from '../../../lib/hooks/use-native-country-name'
import { GenderEnum, genderEnumMapping } from '../../../lib/types/enums/common/gender.enum'
import {
  LanguageEnum,
  languageEnumMapping
} from '../../../lib/types/enums/languages/language.enum'
import { TeflInput } from '../../../lib/types/models/tefl/tefl-input'
import {
  getSelectedEnumOption,
  getSelectedOption
} from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

const TeflDetailsForm = () => {
  const form = useFormContext<TeflInput>()
  useNativeCountryField(form, 'country')
  const languageMapping = languageEnumMapping('en')
  const genderMapping = genderEnumMapping()

  const { data: nationalitiesList } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })

  return (
    <InnerBox className="p-4 rounded-xl">
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        Details
      </Typography>
      <div className="h-4" />

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
            Gender : *
          </Typography>
          <div className={'flex gap-20'}>
            {Object.values(GenderEnum).map((gender) => (
              <FormField
                key={gender}
                control={form.control}
                name="gender"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          label={genderMapping[gender]}
                          checked={field.value === gender}
                          onCheck={() => field.onChange(gender)}
                        />
                      </FormControl>
                    </FormItem>
                  )
                }}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label="Last name" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label="First name" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    required
                    label={'Phone number'}
                    placeholder={'ex : 06 12 34 56 78'}
                    defaultCountry="FR"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="email" label="E-mail" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className={'flex gap-3'}>
          <FormField
            control={form.control}
            name={'birthDate'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput {...field} required label={'Date of birth'} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="nationality"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    label="Nationality"
                    searchable
                    options={nationalitiesList?.map((nationality) => ({
                      label: nationality.label,
                      value: nationality.code
                    }))}
                    selectedOption={getSelectedOption(
                      field.value,
                      nationalitiesList?.find((national) => national.code === field.value)?.label
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="address1"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledAdressInput
                  nativeCountryNames
                  {...field}
                  required
                  label="Address"
                  onDetailsChange={(placeDetails) => {
                    form.setValue('address1', placeDetails.address, { shouldValidate: true })
                    form.setValue('postalCode', placeDetails.postalCode, { shouldValidate: true })
                    form.setValue('city', placeDetails.city, { shouldValidate: true })
                    form.setValue('country', placeDetails.country, { shouldValidate: true })
                    form.trigger(['address1', 'postalCode', 'city', 'country'])
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address2"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="text" label="Address complement" />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label="Town" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="postalCode"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required type="text" label={'Zip code'} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} required type="text" label="Country" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="motherTongue"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  label="Mother tongue"
                  options={Object.values(LanguageEnum).map((language) => ({
                    label: languageMapping[language].label,
                    value: language
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, languageMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </InnerBox>
  )
}

export default TeflDetailsForm
